import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { getFirestore, collection, getDocs } from 'firebase/firestore'

const API_BASE = (import.meta as any).env.VITE_API_URL as string
interface CampaignItem {
  id: string
  productName: string
  status: string
  createdAtSeconds: number
}

type WaState = 'connected' | 'connecting' | 'disconnected' | 'checking'

export default function WhatsAppPage() {
  const navigate = useNavigate()
  const [items, setItems] = useState<CampaignItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [waStatus, setWaStatus] = useState<Record<string, WaState>>({})

  // Modal de conexão (QR code)
  const [modalCampaignId, setModalCampaignId] = useState<string | null>(null)
  const [modalCampaignName, setModalCampaignName] = useState('')
  const [qrCode, setQrCode] = useState<string | null>(null)
  const [modalError, setModalError] = useState('')
  const [justConnected, setJustConnected] = useState(false)

  const statusPollRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const qrRefreshRef = useRef<ReturnType<typeof setInterval> | null>(null)

  useEffect(() => {
    loadCampaigns()
    return () => clearModalIntervals()
  }, [])

  async function loadCampaigns() {
    try {
      const db = getFirestore()
      const snap = await getDocs(collection(db, 'campaigns'))
      const list: CampaignItem[] = []

      snap.forEach(docSnap => {
        const data = docSnap.data() as any
        list.push({
          id: docSnap.id,
          productName: data.briefing?.productName || 'Sem nome',
          status: data.status || 'draft',
          createdAtSeconds: data.createdAt?.seconds || 0,
        })
      })

      list.sort((a, b) => b.createdAtSeconds - a.createdAtSeconds)
      setItems(list)

      // Inicializa todos como "checking" e dispara as checagens em paralelo
      const initialStatus: Record<string, WaState> = {}
      list.forEach(item => { initialStatus[item.id] = 'checking' })
      setWaStatus(initialStatus)

      list.forEach(item => checkStatus(item.id))
   } catch (err: any) {
      setError('Erro ao carregar campanhas: ' + err.message)
    } finally {
      setLoading(false)
    }
  }

  async function checkStatus(campaignId: string) {
    try {
      const res = await fetch(`${API_BASE}/whatsapp/status?campaignId=${campaignId}`)
      const data = await res.json()

      let state: WaState = 'disconnected'
      if (data?.state === 'open') state = 'connected'
      else if (data?.state === 'connecting') state = 'connecting'

      setWaStatus(prev => ({ ...prev, [campaignId]: state }))
      return state
    } catch {
      setWaStatus(prev => ({ ...prev, [campaignId]: 'disconnected' }))
      return 'disconnected' as WaState
    }
  }

  function clearModalIntervals() {
    if (statusPollRef.current) clearInterval(statusPollRef.current)
    if (qrRefreshRef.current) clearInterval(qrRefreshRef.current)
    statusPollRef.current = null
    qrRefreshRef.current = null
  }

  async function fetchNewQr(campaignId: string) {
    try {
      setModalError('')
      const res = await fetch(`${API_BASE}/whatsapp/connect`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ campaignId }),
      })
      const data = await res.json()

      if (!data?.success || !data?.qrcode) {
        setModalError('Não foi possível gerar o QR code. Tente novamente.')
        return
      }
      setQrCode(data.qrcode)
    } catch (err: any) {
      setModalError('Erro ao conectar com o servidor: ' + err.message)
    }
  }

  function openConnectModal(campaignId: string, campaignName: string) {
    setModalCampaignId(campaignId)
    setModalCampaignName(campaignName)
    setQrCode(null)
    setModalError('')
    setJustConnected(false)

    fetchNewQr(campaignId)

    // Atualiza o QR a cada 25s (ele expira em 30-60s na Evolution API)
    qrRefreshRef.current = setInterval(() => {
      fetchNewQr(campaignId)
    }, 25000)

    // Verifica o status a cada 3s; quando conectar, fecha o modal sozinho
    statusPollRef.current = setInterval(async () => {
      const state = await checkStatus(campaignId)
      if (state === 'connected') {
        clearModalIntervals()
        setJustConnected(true)
        setTimeout(() => closeModal(), 1800)
      }
    }, 3000)
  }

  function closeModal() {
    clearModalIntervals()
    setModalCampaignId(null)
    setModalCampaignName('')
    setQrCode(null)
    setModalError('')
    setJustConnected(false)
  }

  function statusBadge(state: WaState | undefined) {
    if (state === 'connected') {
      return (
        <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400 font-medium">
          Conectado
        </span>
      )
    }
    if (state === 'connecting') {
      return (
        <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-500/20 text-yellow-400 font-medium">
          Conectando...
        </span>
      )
    }
    if (state === 'checking') {
      return (
        <span className="text-xs px-2 py-0.5 rounded-full bg-white/10 text-gray-400 font-medium">
          Verificando...
        </span>
      )
    }
    return (
      <span className="text-xs px-2 py-0.5 rounded-full bg-white/10 text-gray-400 font-medium">
        Desconectado
      </span>
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-400">Carregando campanhas...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="bg-red-500/10 border border-red-500/30 text-red-400 rounded-lg px-4 py-3">
          {error}
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-white">WhatsApp</h1>
      </div>

      {items.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-64 text-center">
          <p className="text-gray-400 mb-2">Nenhuma campanha criada ainda.</p>
          <p className="text-gray-500 text-sm">
            Crie uma campanha primeiro para poder conectar o WhatsApp a ela.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {items.map(item => {
            const state = waStatus[item.id]
            const isConnected = state === 'connected'

            return (
              <div
                key={item.id}
                className="bg-white/5 border border-white/10 rounded-2xl p-5 flex items-center justify-between gap-4"
              >
                <div className="min-w-0 cursor-pointer" onClick={() => navigate(`/campanhas/${item.id}`)}>
                  <p className="text-white font-semibold truncate">{item.productName}</p>
                  <div className="flex items-center gap-2 mt-1">
                    {statusBadge(state)}
                  </div>
                </div>

                <div className="flex items-center gap-3 flex-shrink-0">
                  <button
                    onClick={() => openConnectModal(item.id, item.productName)}
                    className="px-4 py-2 rounded-lg text-sm font-semibold text-white"
                    style={
                      isConnected
                        ? { background: 'rgba(255,255,255,0.1)' }
                        : { background: 'linear-gradient(135deg,#6366f1,#10b981)' }
                    }
                  >
                    {isConnected ? 'Reconectar' : 'Conectar WhatsApp'}
                  </button>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {modalCampaignId && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-dark-900 border border-white/10 rounded-2xl p-6 max-w-sm w-full text-center">
            <h2 className="text-white font-semibold mb-1">Conectar WhatsApp</h2>
            <p className="text-gray-400 text-sm mb-4 truncate">{modalCampaignName}</p>

            {justConnected ? (
              <div className="py-8">
                <p className="text-emerald-400 font-semibold">WhatsApp conectado! ✓</p>
              </div>
            ) : modalError ? (
              <div className="py-6">
                <p className="text-red-400 text-sm mb-4">{modalError}</p>
                <button
                  onClick={() => fetchNewQr(modalCampaignId)}
                  className="px-4 py-2 rounded-lg text-sm font-semibold text-white"
                  style={{ background: 'linear-gradient(135deg,#6366f1,#10b981)' }}
                >
                  Tentar novamente
                </button>
              </div>
            ) : qrCode ? (
              <>
                <div className="bg-white rounded-xl p-3 inline-block mb-4">
                  <img src={qrCode} alt="QR Code WhatsApp" className="w-56 h-56" />
                </div>
                <p className="text-gray-400 text-xs mb-1">
                  Abra o WhatsApp no celular → Aparelhos conectados → Conectar um aparelho
                </p>
                <p className="text-gray-500 text-xs">
                  O código atualiza automaticamente antes de expirar.
                </p>
              </>
            ) : (
              <div className="py-12">
                <p className="text-gray-400 text-sm">Gerando QR code...</p>
              </div>
            )}

            <button
              onClick={closeModal}
              className="mt-5 text-gray-400 text-sm hover:text-white transition-colors"
            >
              Cancelar
            </button>
          </div>
        </div>
      )}
    </div>
  )
}